// ---------------------------------------------------------------------------
// P2a: traço do arrasto do tempo (dedo → movimento em px). Puro e testável; o ganho (1 vela por px) fica no tratador.
//
// Um traço pertence ao ponteiro que o começou (pointerdown): a referência (x do dedo) é fixada NO down, de forma
// síncrona, e só o mesmo pointerId move, encerra ou cancela o traço. Referência vazia, NaN ou de outro traço nunca
// entra na conta: o movimento é 0 e a referência passa a ser o x atual (começa do ponto em que o arrasto é
// reconhecido; nunca "x − 0").
//
// Antes (SceneContent): `movimento = e.clientX − timeDragPointerXRef.current`, com a referência verificada como não
// nula ANTES de `e.stopPropagation()`; no r3f esse stopPropagation dispara, na hora, o pointerout/leave dos outros
// objetos em hover (events: cancelPointer), e esses tratadores zeravam a referência: `X − null = X`.
// ---------------------------------------------------------------------------

export const NO_POINTER = -1;
/** Um traço sem movimento há mais que isto pode ser assumido por outro dedo (up/cancel perdido não trava o arrasto). */
export const STROKE_STALE_MS = 1000;

export interface StrokeRefs {
  /** x (px CSS) do dedo usado como base do próximo movimento; null = sem traço */
  x: { current: number | null };
  /** pointerId do dedo que começou o traço; NO_POINTER = sem traço */
  pid: { current: number };
  /** instante (ms) do down ou do último movimento do traço */
  t: { current: number };
}

/** Há traço em andamento? */
export function strokeActive(r: StrokeRefs): boolean { return r.pid.current !== NO_POINTER; }

/** O traço em andamento é deste ponteiro? */
export function strokeOwns(r: StrokeRefs, pid: number): boolean { return r.pid.current !== NO_POINTER && r.pid.current === pid; }

/**
 * pointerdown de `pid` pode começar um traço? Sim sem traço, para o mesmo ponteiro, ou se o traço de outro ponteiro
 * está parado há mais de STROKE_STALE_MS (o up dele se perdeu). Um segundo dedo no meio do traço não o rouba.
 */
export function strokeMayBegin(r: StrokeRefs, pid: number, now: number): boolean {
  return r.pid.current === NO_POINTER || r.pid.current === pid || now - r.t.current > STROKE_STALE_MS;
}

/** pointerdown: o traço passa a ser deste ponteiro, com a referência no x do toque. */
export function strokeBegin(r: StrokeRefs, pid: number, x: number, now: number): void {
  r.pid.current = pid;
  r.x.current = Number.isFinite(x) ? x : null;
  r.t.current = now;
}

/**
 * pointermove: movimento em px desde a referência; a referência passa a ser `x`. Outro ponteiro ou sem traço: 0 e
 * nada muda. Referência vazia ou inválida: 0 e a referência passa a ser `x`.
 */
export function strokeStep(r: StrokeRefs, pid: number, x: number, now: number): number {
  if (!strokeOwns(r, pid)) return 0;
  r.t.current = now;
  const ref = r.x.current;
  if (!Number.isFinite(x)) return 0;
  r.x.current = x;
  if (ref === null || !Number.isFinite(ref)) return 0;
  return x - ref;
}

/**
 * pointerup / pointercancel / lostpointercapture: encerra o traço se for do mesmo ponteiro (ou de qualquer um, sem
 * `pid`). Devolve true se encerrou (ou se já não havia traço).
 */
export function strokeEnd(r: StrokeRefs, pid?: number): boolean {
  if (pid !== undefined && r.pid.current !== NO_POINTER && r.pid.current !== pid) return false;
  r.pid.current = NO_POINTER;
  r.x.current = null;
  return true;
}
